import React from 'react';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';

import Lozenge from '@atlaskit/lozenge';

import LinkIcon from '@atlaskit/icon/core/link';

import { Text, Inline, Stack } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';

import { type TypeDefinition, lookupTypeByTypeId } from '../../../common/application-types.tsx';
import ProductCard from '../common/product-card/index.tsx';
import {
	autoConfigureApplinkConsumer,
	putApplink,
	putApplinkOAuth2LOProvider,
	putApplinkOAuth3LOProvider,
} from '../../../rest/index.tsx';
import type {
	ApplicationLinkCreationEntity,
	ApplicationLinkManifest,
} from '../../../common/entities.tsx';
import {
	STATUS_RECIPROCAL_SUCCESS,
	generateApplinksCreationRedirectionUrl,
	updateStatusLog,
	type CreationState,
} from '../../../common/interchange.tsx';
import type { StageReciprocalReviewProps, StageReviewProps } from '../../../common/types.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
});

type ReviewSubpageProps = {
	state: StageReviewProps | StageReciprocalReviewProps;
	onSuccess: (data: {
		oldState: StageReviewProps | StageReciprocalReviewProps;
		createdResource: string;
	}) => void;
	onError: (error: string) => void;
	onCancel: () => void;
};

const ReviewSubpage = (props: ReviewSubpageProps) => {
	useIntl();

	const reciprocalMode = props.state.kind === 'reciprocal-review';

	const thisApplicationManifest: ApplicationLinkManifest = props.state.localManifest;
	const targetApplicationManifest: ApplicationLinkManifest = props.state.remoteManifest;

	// TODO: Determine if the type is DC or Cloud
	const thisApplicationType: TypeDefinition = lookupTypeByTypeId(
		thisApplicationManifest.typeId,
		false,
	);
	const targetApplicationType: TypeDefinition = lookupTypeByTypeId(
		targetApplicationManifest.typeId,
		false,
	);

	const onFormSubmit = async () => {
		const applinkCreationEntity: ApplicationLinkCreationEntity = {
			id: targetApplicationManifest.id,
			name: targetApplicationManifest.name,
			displayUrl: targetApplicationManifest.url,
			rpcUrl: targetApplicationManifest.url,
			typeId: targetApplicationManifest.typeId,
			isPrimary: false,
			isSystem: false,
			isOAuth2ThreeLoEnabled: false,
		};

		try {
			const response = await putApplink(applinkCreationEntity);

			if (
				props.state.kind === 'reciprocal-review' &&
				props.state.inboundCreationState.applinkOriginalCreatedId
			) {
				// Configure OAuth
				await putApplinkOAuth3LOProvider(targetApplicationManifest.id);
				await autoConfigureApplinkConsumer(targetApplicationManifest.id);
				await putApplinkOAuth2LOProvider(targetApplicationManifest.id);

				// Update status log
				const updatedApplinkCreationStatusLog = updateStatusLog(
					props.state.inboundCreationState.applinkCreationStatusLog,
					{ status: STATUS_RECIPROCAL_SUCCESS },
				);

				const newCreationState: CreationState = {
					applinkCreationStatusLog: updatedApplinkCreationStatusLog,
					applinkOriginalCreatedId: props.state.inboundCreationState.applinkOriginalCreatedId,
					sharedUserbase: props.state.inboundCreationState.sharedUserbase,
					applinkConfigInPreAuth: true,
					applinkConfigInServerAuth: true,
					applinkConfigInUserAuth: true,
					applinkConfigOutPreAuth: true,
					applinkConfigOutServerAuth: true,
					applinkConfigOutUserAuth: true,
				};

				const newRedirectionUrl = generateApplinksCreationRedirectionUrl(
					targetApplicationManifest.url,
					targetApplicationManifest.typeId,
					newCreationState,
				);

				// TODO: Maybe have a button?
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.href = newRedirectionUrl;
				return;
			}

			props.onSuccess({
				oldState: props.state,
				createdResource: response['resources-created'][0].href,
			});
		} catch (error) {
			if (error instanceof Error) {
				props.onError(error.message);
			} else {
				props.onError('An unknown error occurred while fetching the application link manifest.');
			}
			return error;
		}
	};

	return (
		<Form onSubmit={onFormSubmit}>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<FormHeader title={reciprocalMode ? 'Review incoming link request' : 'Review link'}>
						{!reciprocalMode && (
							<Text>
								Confirm the details of the application link you are creating. This will allow the
								remote <Text as="strong">{targetApplicationType.name}</Text> to access this Jira
								site. You will later be redirected to {targetApplicationType.name} to complete the
								other side of the link.
							</Text>
						)}
						{reciprocalMode && (
							<Text>
								The remote <Text as="strong">{targetApplicationType.name}</Text> application has
								requested to create a link to this Jira site. Review the details below before
								completing the link. This will allow the remote {targetApplicationType.name} to
								access this Jira site.
							</Text>
						)}
					</FormHeader>

					<Stack xcss={styles.linkDisplay} space="space.200">
						<ProductCard
							applicationType={thisApplicationType}
							displayUrl={thisApplicationManifest.url}
							heading="This application"
						/>
						<Inline space="space.100" alignBlock="center" alignInline="center">
							<LinkIcon label="Link icon" />
							{reciprocalMode && <Lozenge appearance="inprogress">Partially Connected</Lozenge>}
							{!reciprocalMode && <Lozenge>Not Connected</Lozenge>}
						</Inline>
						<ProductCard
							applicationType={targetApplicationType}
							displayUrl={targetApplicationManifest.url}
							heading="Remote application"
						/>
					</Stack>

					<FormFooter>
						<ButtonGroup label="Form submit options">
							<Button appearance="subtle" onClick={props.onCancel}>
								Cancel
							</Button>
							<Button type="submit" appearance="primary" isLoading={submitting}>
								{reciprocalMode ? 'Complete link' : 'Create link'}
							</Button>
						</ButtonGroup>
					</FormFooter>
				</form>
			)}
		</Form>
	);
};

export default ReviewSubpage;
